import { useState, useEffect, useCallback } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { CareMapTagsClient, IExistingTag } from './CareMapTagsClient';

export type ExistingTag = IExistingTag;

interface UseExistingCareMapTagsResult {
    tags: ExistingTag[];
    loading: boolean;
    error: string | null;
    refresh: (sortBy?: 'count' | 'recency', forceRefresh?: boolean) => Promise<void>;
    getTagsByRecency: () => Promise<ExistingTag[]>;
    invalidateCache: () => void;
}

/**
 * Hook to fetch and manage existing tags from the current care map via API
 */
// Simple session cache for tags with size limit
const tagsCache = new Map<string, { tags: ExistingTag[]; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 50; // Limit cache to 50 entries

// Helper to clean up expired cache entries and enforce size limit
const cleanupCache = () => {
    const now = Date.now();
    const entries = Array.from(tagsCache.entries());

    // Remove expired entries
    entries.forEach(([key, value]) => {
        if (now - value.timestamp > CACHE_DURATION) {
            tagsCache.delete(key);
        }
    });

    // If still over limit, remove oldest entries
    if (tagsCache.size > MAX_CACHE_SIZE) {
        const sortedEntries = Array.from(tagsCache.entries())
            .sort((a, b) => a[1].timestamp - b[1].timestamp);

        const entriesToRemove = sortedEntries.slice(0, tagsCache.size - MAX_CACHE_SIZE);
        entriesToRemove.forEach(([key]) => tagsCache.delete(key));
    }
};

export const useExistingCareMapTags = (): UseExistingCareMapTagsResult => {
    const [tags, setTags] = useState<ExistingTag[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const careMapState = useInstance(CareMapState);
    const tagsClient = useInstance(CareMapTagsClient);

    const fetchTags = useCallback(async (sortBy: 'count' | 'recency' = 'count', forceRefresh = false): Promise<ExistingTag[]> => {
        const careMap = careMapState.getCareMap();
        if (!careMap?.links?.tags) {
            return [];
        }

        const cacheKey = `${careMap.userId}/${careMap.careMapId}/${sortBy}`;
        const cached = tagsCache.get(cacheKey);
        const now = Date.now();

        // Return cached data if it exists, isn't expired, and we're not forcing refresh
        if (!forceRefresh && cached && (now - cached.timestamp) < CACHE_DURATION) {
            return cached.tags;
        }

        try {
            const response = await tagsClient.getExistingTags({
                careMap,
                sortBy
            });

            // Clean up cache before adding new entry
            cleanupCache();

            // Cache the response
            tagsCache.set(cacheKey, { tags: response.tags, timestamp: now });

            return response.tags;
        } catch (err) {
            throw err;
        }
    }, [careMapState, tagsClient]);

    const refresh = useCallback(async (sortBy: 'count' | 'recency' = 'count', forceRefresh = false) => {
        setLoading(true);
        setError(null);

        try {
            const fetchedTags = await fetchTags(sortBy, forceRefresh);
            setTags(fetchedTags);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load tags');
            setTags([]);
        } finally {
            setLoading(false);
        }
    }, [fetchTags]);

    // Method to invalidate cache when tags change
    const invalidateCache = useCallback(() => {
        const careMap = careMapState.getCareMap();
        if (careMap) {
            const baseKey = `${careMap.userId}/${careMap.careMapId}`;
            // Clear both count and recency cache entries
            tagsCache.delete(`${baseKey}/count`);
            tagsCache.delete(`${baseKey}/recency`);
        }
    }, [careMapState]);

    const getTagsByRecency = useCallback(async (): Promise<ExistingTag[]> => {
        try {
            return await fetchTags('recency');
        } catch (err) {
            return [];
        }
    }, [fetchTags]);

    // Load tags when care map changes, using cache if available
    useEffect(() => {
        const careMap = careMapState.getCareMap();
        if (careMap) {
            const loadTags = async () => {
                // Try to get cached data first without showing loading
                const cacheKey = `${careMap.userId}/${careMap.careMapId}/count`;
                const cached = tagsCache.get(cacheKey);
                const now = Date.now();

                if (cached && (now - cached.timestamp) < CACHE_DURATION) {
                    // Use cached data immediately without loading state
                    setTags(cached.tags);
                } else {
                    // No cache or expired, fetch fresh data
                    refresh();
                }
            };

            loadTags();
        }
    }, [careMapState, refresh]);

    return {
        tags,
        loading,
        error,
        refresh,
        getTagsByRecency,
        invalidateCache
    };
};
